<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-experience-and-automation-page" element-loading-text="Loading...">
    <!-- <div class="exp-and-auto-page-title-small">Home</div> -->
    <div class="button-flex">
      <div class="exp-and-auto-page-title">Experience & Automation Summary</div>
      <div class="question-mark">
        <wy-page-tooltip />
      </div>
      <!-- <div slot="content"><span v-html="tooltipForButtons"></span></div>
        <div class="header-icon-info"></div>
      </el-tooltip> -->
    </div>

    <div ref="panelsContainer" class="components-container">
      <keep-alive v-for="(panel, index) in activePanels" :key="index">
        <component :ref="panel.key" :id="panel.key" class="panel-component-section" :is="panel.component" :controller="module" :panelTitle="panel.name" v-bind="panel.properties"></component>
      </keep-alive>
    </div>

    <div class="exp-and-auto-table-section">
      <div class="top-flex">
        <div :class="overviewData ? 'left' : 'no-data-panel-type-container'">
          <div class="panel-title">{{ panelTitle }}</div>
          <span v-if="selection != 'all'" :class="overviewData ? 'panel-type' : 'no-data-panel-type'">({{ displaySection(selection) }})</span>
        </div>
        <div class="flex-container">
          <wy-exp-and-auto-header-popup
            :selecteddatePeriodRange="viewByHeader"
            :panelTitleNoBracket="panelTitle"
            :showChartViewBy="showChartViewBy"
            v-show="overviewData"
            ref="settingSection"
            :controller="module"
          />
        </div>
      </div>

      <div class="table-section" style="overflow: hidden;" v-loading="loading || tableLoading">
        <wy-table-list ref="table" :controller="module" :enableSelection="true" :defaultSort="{ name: 'totalVolume', type: 1 }" searchFieldName="contact reaons" />
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import WyUtils from '../../library/wy-utils.js';
import WyPanelHeader from './wy-exp-and-auto-header.vue';
import WyHomeExpAndAutoPanelBesBasSummary from './panel-templates/v1/wy-home-experience-automation-panel-bes-bas-summary.vue';
import WyHomeExpAndAutoPanelBesBasTrend from './panel-templates/v1/wy-home-experience-automation-panel-bes-bas-trend.vue';
import WyHomeExpAndAutoPanelConversationVolumeSummary from './panel-templates/v1/wy-home-experience-automation-panel-conversation-volume-summary.vue';
import WyHomeExpAndAutoPanelConversationVolumeTrend from './panel-templates/v1/wy-home-experience-automation-panel-conversation-volume-trend.vue';
import WyTableList from '../widgets/wy-table-list';
import Sortable from 'sortablejs';
import WyPanelHeaderSettintPopup from './wy-exp-and-auto-header-popup';
import wyPageTooltip from '../widgets/wy-page-tooltip.vue';

export default Vue.extend({
  name: 'wy-experience-and-automation-page',
  components: {
    'wy-home-experience-automation-panel-bes-bas-summary': WyHomeExpAndAutoPanelBesBasSummary,
    'wy-home-experience-automation-panel-bes-bas-trend': WyHomeExpAndAutoPanelBesBasTrend,
    'wy-home-experience-automation-panel-conversation-volume-summary': WyHomeExpAndAutoPanelConversationVolumeSummary,
    'wy-home-experience-automation-panel-conversation-volume-trend': WyHomeExpAndAutoPanelConversationVolumeTrend,
    'wy-table-list': WyTableList,
    'wy-exp-and-auto-header': WyPanelHeader,
    'wy-exp-and-auto-header-popup': WyPanelHeaderSettintPopup,
    'wy-page-tooltip': wyPageTooltip,
  },

  data() {
    return {
      module: this,
      pagePanels: [
        'wy-home-experience-automation-panel-bes-bas-summary',
        'wy-home-experience-automation-panel-bes-bas-trend',
        'wy-home-experience-automation-panel-conversation-volume-summary',
        'wy-home-experience-automation-panel-conversation-volume-trend',
      ],
      loading: false,
      rawData: {},
      viewByHeader: 'Last 30 days',
      showChartViewBy: false,
      loadTables: false,
      dropdownStartDate: '',
      dropdownEndDate: '',
      tableLoading: false,
      showTable: false,
      panelTitle: 'Conversation topics for VA conversations',
      overviewData: {},
      selection: 'all',
      tooltipForButtons:
        "This page provides a summary of your virtual agent's experience and automation scores.<br> It displays your bot's scores for the last 30 days and 3 months, a breakdown by conversation topics,<br> as well as a trend for conversations contained and handed off.<br><br>BAS is an indicator for how well you are automating conversations.<br> The score is based on escalations, negative feedback, abandonment and false-positives<br> that can occur within a conversation.<br><br>BES is an indicator for customer experience throughout a virtual agent conversation. <br>The score is based on abandonment, sentiment, feedback, bot repetition or paraphrasing and the use of profanity.",

      columns: [
        {
          label: 'Name',
          name: 'topicDisplayName',
          mandatory: true,
          selected: false,
          sortable: true,
          group: 'Conversation Topics',
          align: 'left',
          minWidth: 383,
          maxWidth: 383,
          fixed: true,
          mainColumn: true,
          copyable: false,
          isEditTopicName: false,
          extendableField: true,
        },

        { label: 'Volume', name: 'totalVolume', selected: true, sortable: true, group: 'Volume', minWidth: 100, suffix: '', align: 'center' },
        { label: 'Trend', type: 'trend', name: 'volumeTrendGraph', selected: true, sortable: false, group: 'Volume', minWidth: 180 },
        { label: '% of total', name: 'volumePercent', selected: true, sortable: true, group: 'Volume', sortBy: 'volumePercent', minWidth: 120, suffix: '%', align: 'right' },

        { label: 'BES', name: 'besPercent', selected: true, sortable: true, suffix: '', group: 'BES', sortBy: 'besPercentOrigin', minWidth: 100, align: 'center' },
        { label: 'Trend', type: 'trend', name: 'besTrendGraph', selected: true, sortable: false, group: 'BES', minWidth: 180 },
        { label: 'Target', type: 'target', name: 'besTarget', selected: true, sortable: false, suffix: '', group: 'BES', minWidth: 120, align: 'right' },

        { label: 'BAS', name: 'basPercent', selected: true, sortable: true, suffix: '%', group: 'BAS', sortBy: 'basPercentOrigin', minWidth: 100, align: 'right' },
        { label: 'Trend', type: 'trend', name: 'basTrendGraph', selected: true, sortable: false, group: 'BAS', minWidth: 180 },
        { label: 'Target', type: 'target', name: 'basTarget', selected: true, suffix: '%', sortable: false, group: 'BAS', minWidth: 120, align: 'right' },
      ],
    };
  },

  computed: {
    ...mapGetters(['dataModel', 'isLoggedIn', 'config', 'companyConfig', 'currentPagePanels', 'currentPageName', 'defaultTarget', 'companyProfileSettings']),

    activePanels() {
      const pagePanelComponents = [];
      for (const pagePanel of this.pagePanels) {
        const panel = this.config.appPanels.find((el) => el.key == pagePanel);
        if (panel && panel.enabled) {
          pagePanelComponents.push({
            key: panel.key,
            name: panel.name,
            properties: panel.properties,
            component: panel.key,
          });
        }
      }
      return pagePanelComponents;
    },
  },

  watch: {
    loading: function(val) {
      setTimeout(() => {
        val ? (this.showTable = false) : (this.showTable = true);
      }, 300);
    },
  },

  methods: {
    ...mapMutations(['updateDataModelColumns']),
    ...mapActions(['fetchBesBasContactReasonsSummary', 'prepareExpAndAutoPageViewByData', 'getPagePanels', 'updatePagePanels']),

    handlePerformanceSelection(selection) {
      if (this.selection != selection) {
        this.selection = selection;
        this.handleFilterChange();
      }
    },
    displaySection(section) {
      if (section == 'contained') {
        return 'Intents to contain';
      }
      if (section == 'handoff') {
        return 'Intents to handoff';
      }
      return '';
    },

    async changeDatePeriod(sDate, eDate, dateRangeType) {
      this.startDate = sDate;
      this.endDate = eDate;
      this.viewByHeader = dateRangeType;
      this.tableLoading = true;
      if ((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.overviewData = await this.fetchContactReasonsSummary(sDate, eDate);
        this.updateDataModelColumns({ columns: this.columns });
        if (this.$refs.table) {
          this.$refs.table.refreshTable({ disablePageLoading: true });
        } else {
          this.loading = false;
        }
        this.tableLoading = false;
      }
    },

    async filterNumberRangeChanged(dateRange, rawData, templateData, view) {
      view.pannelData = await this.prepareExpAndAutoPageViewByData({ viewBy: dateRange, rawData: rawData, templateData: templateData, companyConfig: this.companyConfig });
      view.sectionData = view.pannelData.data[view.dataIndex];
      this.refreshOverviewPanel(view, view.sectionData);
    },

    async fetchContactReasonsSummary(sDate, eDate) {
      // DEMO: for demo purpose
      const demoConfigs = this.companyConfig.demoConfigs || {};
      const demoDateRange = demoConfigs[this.viewByHeader];
      if (demoDateRange && demoDateRange.startDate && demoDateRange.endDate) {
        sDate = demoDateRange.endDate;
        eDate = demoDateRange.startDate;
      }
      if (this.companyProfileSettings && this.companyProfileSettings.targetSettings && this.companyProfileSettings.targetSettings.settings.home) {
        this.dropdownStartDate = sDate;
        this.dropdownEndDate = eDate;

        let targetConfig = {};
        if (
          this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary &&
          this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.default &&
          this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.default.target
        ) {
          targetConfig.default = this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.default.target;
        }

        if (
          this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary &&
          this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.contactReasons &&
          this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.contactReasons.length > 0
        ) {
          let contactReasonsTargets = this.companyProfileSettings.targetSettings.settings.home.experienceAutomationSummary.contactReasons;
          contactReasonsTargets.forEach((item) => {
            Object.keys(item.target).forEach((targetKey) => {
              targetConfig[item.key] = item.target;
              if (item.target[targetKey].value == null && targetConfig.default && targetConfig.default[targetKey]) {
                targetConfig[item.key][targetKey].value = targetConfig.default[targetKey].value;
              }
            });
          });
        }
        return this.fetchBesBasContactReasonsSummary({ sDate: sDate, eDate: eDate, targetRange: targetConfig });
      } else {
        this.dropdownStartDate = sDate;
        this.dropdownEndDate = eDate;
        return this.fetchBesBasContactReasonsSummary({ sDate: sDate, eDate: eDate });
      }
    },

    async handleFilterChange() {
      this.$children.map((component) => {
        if (component.handleFilterChange) {
          component.handleFilterChange();
        }
      });
      this.tableLoading = true;
      if ((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
        this.overviewData = await this.fetchContactReasonsSummary(this.dropdownStartDate, this.dropdownEndDate);
        this.tableLoading = false;
        this.updateDataModelColumns({ columns: this.columns });
        if (this.$refs.table) {
          this.$refs.table.refreshTable({ disablePageLoading: true });
        } else {
          this.loading = false;
        }
      }
    },

    refreshOverviewPanel(view, data) {
      if (view) {
        //prepare view by data
        view.pannelData.data.forEach((sectionData) => {
          if (sectionData.key == view.sectionData.key) {
            sectionData.selected = true;
          }
        });
        if (data.multiChart) {
          if (view.$refs.tabSection.$refs.lineChart || view.$refs.tabSection.$refs.pieChart) {
            // if(data.lineChartData) view.$refs.tabSection.$refs.lineChart.buildChart();
            if (data.pieChartData) view.$refs.tabSection.$refs.pieChart.buildChart();
            if (data.columnChartData) view.$refs.tabSection.$refs.comboChart.buildChart();
          }
        } else if (view.$refs.tabSection.$refs.lineChart || view.$refs.tabSection.$refs.pieChart) {
          if (data.lineChartData) {
            view.$refs.tabSection.$refs.lineChart.buildChart();
          }
          if (data.pieChartData) view.$refs.tabSection.$refs.pieChart.buildChart();
          if (data.columnChartData) view.$refs.tabSection.$refs.columnChart.buildChart();
        }
      }
    },
    async reLoadTable() {
      this.tableLoading = true;
      this.overviewData = await this.fetchContactReasonsSummary(this.dropdownStartDate, this.dropdownEndDate);
      this.tableLoading = false;
      this.updateDataModelColumns({ columns: this.columns });
      if (this.$refs.table) {
        this.$refs.table.refreshTable({ disablePageLoading: true });
      } else {
        this.loading = false;
      }
    },
  },

  mounted() {
    this.$root.registerTooltips(this.$el);

    Sortable.create(this.$refs.panelsContainer, {
      handle: '.move-info',
      onUpdate: (evt) => {
        let sortedPanels = [];
        for (const el of evt.to.childNodes) {
          sortedPanels.push(el.id);
        }
        this.updatePagePanels(sortedPanels);
      },
    });
    this.updateDataModelColumns({ columns: this.columns });
  },

  async created() {
    this.$router.currPageComponent = this;
    this.initDate = new Date();
    // listen events from other components
    this.$root.registerPageEvent(this.$route.name, (event) => {
      if (this[event.name]) this[event.name]();
    });

    this.pagePanels = this.currentPagePanels(this.pagePanels);

    if ((!this.loading && this.dataModel.dateEnd) || this.dataModel.dateEnd) {
      this.loading = true;
      await this.fetchContactReasonsSummary();

      if (this.$refs.table) {
        this.$refs.table.refreshTable({ disablePageLoading: true });
      } else {
        this.loading = false;
      }
    }
  },

  destroyed() {
    this.$root.unregisterTooltips(this.$el);
    if (this.isLoggedIn) {
      this.$store.dispatch('amplitudeLogWithFilters', { name: 'exp_and_auto_page_time_spent', data: { time_spent: WyUtils.getTimeDiff(this.initDate) } });
    }
  },
});
</script>

<!-- <style lang="scss">
.gauge-panel-legacy {
  border: 1px solid #cad2da;
  border-radius: 4px;

  display: flex;
  flex-direction: column;

  .panel-header {
    padding: 16px;
    font-size: 1vw;
    border-bottom: 1px solid #cad2da;
  }
  .panel-section {
    padding: 48px;
    padding-bottom: 44px;
    display: flex;
    justify-content: space-between;

    .panel-column {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .panel-title {
        font-size: 24px;
      }

      .panel-info {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        .panel-gauge-name {
          margin-top: -16px;
        }
        .panel-reference {
          font-size: 32px;
        }
        .critical {
          color: #c11149;
        }
        .success {
          color: #186731;
        }
        .panel-target {
          margin-top: -8px;
          font-size: 12px;
        }
      }
    }
  }
}
.wy-exp-and-auto-page-panel-conversations-by-devices.panel-component-section.panel-component-section-force.full-size {
  overflow: hidden;
}
.overview-page-button-poppers {
  font-family: Helvetica Neue, Helvetica;
  font-size: 14.22px;
  font-weight: 400;
}
.exp-and-auto-table-section {
  .el-loading-mask .el-loading-spinner {
    margin-top: 12%;
    top: unset !important;
  }
}
.wy-home-page .wy-container-main {
  .el-loading-mask .el-loading-spinner {
    // margin-top: 12%;
    // top: unset !important;
  }
  &::-webkit-scrollbar {
    width: 20px;
  }
  &::-webkit-scrollbar-thumb {
    border-radius: 160px;
    border: 2px solid transparent;
    border-top: 4px solid transparent;
    background-clip: content-box;
    background-color: #cad2da;
  }
  &::-webkit-scrollbar-track {
    background: #ffffff;
    border-top: 1px solid #cad2da;
  }
  &::-webkit-scrollbar:vertical {
    width: 14px;
  }
  &::-webkit-scrollbar-thumb:vertical {
    border-radius: 160px;
    border: 2px solid transparent;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    background-clip: content-box;
    background-color: #cad2da;
  }
  &::-webkit-scrollbar-track:vertical {
    border-top: none !important;
  }
}

.wy-experience-and-automation-page {
  min-height: calc(100% - 30px);
  padding-right: 0px;
  font-weight: 500;
  font-size: 16px;
  // padding-top: 7px;
  overflow-x: hidden;
  overflow-y: hidden;
  .wy-table-list thead .wy-sub-header th.sortable:hover {
    color: var(--site-font-secondary-color);
  }
  .wy-table-list thead .wy-sub-header th.sortable:hover {
    // background-color: white;
    background-color: #f5f8fa;
  }
  .wy-table-list .el-no-data {
    margin-left: 14px;
  }
  .no-data-small-title {
    font-size: 14px;
  }
  .no-data-sub-title {
    color: var(--site-font-secondary-color);
    font-size: 25px;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 600;
  }

  .exp-and-auto-table-section {
    margin-bottom: 50px;
    margin: 10px 0px 20px 0px;
    width: unset;
    min-height: 512px;
    border: 1px solid #cad2da;
    border-radius: 5px;
    // height: 1175px;

    .table-section {
      border-radius: 6px;
    }
    .wy-table-list {
      min-height: 449px;
      width: calc(100% + 2px) !important;
      position: relative;
      background-color: #fff;
      border: 0px solid #fff;
      // height: 1110px;
      // border-left: 1px solid #FFF;
      // border-right: 1px solid #FFF;
      left: -1px;
    }

    .el-loading-mask {
      width: 100% !important;
      border-top: 1px solid #cad2da;
      // left: 1px;
      // height: calc(100% - 2px) !important;
      // height: 600px !important;
    }
    .header-info-poppers {
      font-family: Helvetica Neue, Helvetica;
      font-size: 14px;
    }
    .flex-container {
      display: flex;
      justify-content: space-evenly;
      align-items: center;
    }

    .top-flex {
      display: flex;
      padding: 15px;
      background-color: #fff;
      padding-bottom: 4px;
      // border-bottom: 1px solid #CAD2DA;
      font-size: 18px;
      justify-content: space-between;
      margin-top: 3px;
      height: 42px;
      // border: 1px solid #CAD2DA;
      // border-top-right-radius: 15px;
      // border-top-left-radius: 15px;
      // border-bottom: 1px solid transparent;
    }
    .bottom-left-stats-small-flex {
      display: flex;
      margin-top: 15px;
      align-items: center;
    }
    .icon-info {
      display: inline-block;
      background: url('../../assets/icons/icon_info_performance_overview.svg') no-repeat;
      width: 20px;
      height: 17px;
      position: relative;
      top: 8px;
      margin-left: 0.25vw;
    }
    .panel-title {
      // font-size: 1.2vw;
      font-size: 18px;
      font-family: Helvetica Neue, Helvetica;
      font-weight: 500;
      color: var(--site-font-secondary-color);
      display: inline-block;
      position: relative;
      top: 4px;
    }
    span.panel-type {
      // padding: 8px;
      font-size: 18px;
      position: relative;
      top: 4px;
    }
    .no-data-panel-type-container {
      display: flex;
      align-items: center;
      margin-bottom: 16px;
    }
    .no-data-panel-type {
      font-size: 18px;
      position: relative;
      top: 4px;
      margin-left: 10px;
    }

    .el-input--mini .el-input__inner {
      height: 31px;
      width: 4.8vw;
      line-height: 1.34vw;
    }
    .setting-arrow-down {
      display: none;
    }
    .el-button.is-plain:hover,
    .el-button.is-plain:active {
      background: #fff;
      border: none;
      color: var(--site-font-tertiary-color);
    }
    .el-button {
      font-family: 'Helvetica Neue', Helvetica !important;
      border: none !important;
      font-size: 14.22px;
      color: var(--site-font-tertiary-color);
      font-weight: normal;
      margin-right: 0.5vw;
    }
    img.el-tooltip.move-info {
      width: 30px;
      position: relative;
      cursor: move;
      bottom: 6px;
    }
    .el-dropdown {
      margin-right: 15px;
    }
    .wy-exp-and-auto-header-popup.right {
      display: flex;
      display: none;
      // visibility: hidden;
    }
    .wy-exp-and-auto-header-popup.right.show {
      display: flex !important;
      // visibility: visible;
    }
    button.el-button.gear-dropdowns.el-button--default.is-plain.el-dropdown-selfdefine {
      border: none !important;
    }
    .wy-table-list thead .wy-header {
      display: none;
    }
    .wy-table-list .wy-table-main thead tr :first-child {
      padding-left: 17px;
    }
    .wy-table-list thead tr th {
      text-align: left;
      color: var(--site-font-secondary-color);
      padding: 25px 10px;
      text-align: center;
      font-size: 14.5px;
      padding-left: 11px;
      font-weight: 500;
      background-color: #f5f8fa;
    }
    .wy-table-list .wy-table-wrapper {
      position: relative;
      border-top-left-radius: unset !important;
      border-top-right-radius: unset !important;

      .wy-page-nodata-with-available-date-range {
        margin: 4%;
        margin-top: 13%;
      }
    }

    .wy-table-list .wy-table-main thead .wy-sub-header th.lastChild {
      text-align: center !important;
    }

    .wy-table-list .wy-table-main tbody tr td.last-child {
      text-align: center !important;
      div {
        padding-right: 0px;
      }
    }

    .wy-table-main tbody {
      overflow: scroll;
      height: calc(100vh - 398px);
      // min-height: 528px;
      width: calc(100% - 2px);
      border-right: 0px solid #eee;
      border-bottom: unset;
      &::-webkit-scrollbar {
        width: 20px;
      }
      &::-webkit-scrollbar-thumb {
        border-radius: 160px;
        border: 2px solid transparent;
        border-top: 4px solid transparent;
        background-clip: content-box;
        background-color: #cad2da;
      }
      &::-webkit-scrollbar-track {
        background: #fff;
        border-top: 1px solid #cad2da;
      }
      &::-webkit-scrollbar:vertical {
        width: 14px;
      }
      &::-webkit-scrollbar-thumb:vertical {
        border-radius: 160px;
        border: 2px solid transparent;
        border-left: 4px solid transparent;
        background-clip: content-box;
        background-color: #cad2da;
      }
      &::-webkit-scrollbar-track {
        background: #ffffff;
        border-top: 1px solid #cad2da;
      }
      &::-webkit-scrollbar:vertical {
        width: 14px;
      }
      &::-webkit-scrollbar-thumb:vertical {
        border-radius: 160px;
        border: 2px solid transparent;
        border-left: 4px solid transparent;
        background-clip: content-box;
        background-color: #cad2da;
      }
      &::-webkit-scrollbar-track:vertical {
        border-left: 1px solid #cad2da;
        border-right: 1px solid #fff;
        border-top: none !important;
      }
    }
    .wy-table-main {
      width: 100%;
      // border-top: 1px solid #eee;
      max-width: 100%;
    }
    .wy-table-list {
      width: 100%;
      position: relative;
      th.lastChild.sortable.fixed.descending.intent {
        text-align: left;
      }
    }
    th.lastChild.sortable.fixed.descending.topicDisplayName::after {
      // this reserves space for the up-arrow/down-arrow icon (not shown yet)
      content: '';
      display: inline-block;
      height: 15px;
      background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
      width: 15px;
      position: relative;
      top: 4px;
      left: 7px;
      background-repeat: no-repeat;
    }
    th.lastChild.sortable.fixed.ascending.topicDisplayName::after {
      // this reserves space for the up-arrow/down-arrow icon (not shown yet)
      content: '';
      display: inline-block;
      height: 15px;
      background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
      width: 15px;
      position: relative;
      top: 4px;
      left: 7px;
      background-repeat: no-repeat;
    }
    .wy-sub-header {
      th.sortable::after {
        // this reserves space for the up-arrow/down-arrow icon (not shown yet)
        content: '';
        display: inline-block;
        height: 15px;
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
        width: 15px;
        position: relative;
        top: 4px;
        left: 7px;
        background-repeat: no-repeat;
      }
    }
    th.sortable.sorted.ascending::after {
      background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
    }
    th.sortable.sorted.descending::after {
      background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
    }
    th.sortable.sorted.ascending:hover::after {
      background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
    }
    th.sortable.sorted.descending:hover::after {
      background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
    }
    th.sortable:hover {
      background-color: #e1e6ec;
      cursor: pointer;
      color: var(--site-font-secondary-color);
      .sort-caret.ascending {
        background-image: url('../../assets/icons/icon_response_perf_sort_ascend.svg') !important;
      }
      .sort-caret.descending {
        background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
      }
    }
    th.sortable:not(.sorted):hover::after {
      background-image: url('../../assets/icons/icon_response_perf_sort_descend.svg') !important;
    }
    th:not(.sortable) {
      span {
        padding-left: 0;
      }
    }
    .wy-table-list .wy-table-fixed {
      // border-top: 1px solid #eee;
      border-left: none !important;
      border-left: 1px solid #eee;
      border-top-left-radius: 15px;
    }

    .wy-table-list {
      .el-button:focus,
      .el-button:hover {
        border-color: transparent !important;
        background-color: transparent !important;
      }
    }
    .wy-table-list .wy-table-fixed tbody td:nth-last-child(1) {
      padding-right: 10px;
    }
    .wy-table-list .wy-table-fixed tbody td:nth-last-child(1),
    .wy-table-list tbody tr td {
      font-weight: 500 !important;
      color: var(--site-font-tertiary-color) !important;
      border-bottom: 1px solid #cad2da;
      font-size: 14.22px !important;
      div {
        align-items: center;
      }
    }
    .wy-table-list .wy-table-wrapper {
      border-top: 1px solid #cad2da;
      border-bottom: 0px;
      width: unset !important;
    }
    .wy-table-list tbody tr td.last-child {
      border-right: none;
      text-align: left !important;
      padding-left: 15px !important;
    }

    .wy-table-list thead,
    th,
    .wy-table-fixed tbody tr td:first-child {
      background-color: white;
      border-bottom: 0px solid transparent;
    }
    .show-fixed-table-shadow {
      box-shadow: 5px -2px 10px -10px grey;
      border-right: none !important;
    }
    .regular-fixed-thead-style {
      background-color: white !important;
      border-bottom: 1px solid transparent;
      border-right: 1px solid #cad2da !important;
    }
    .wy-table-list thead .wy-sub-header th.lastChild {
      border-right: 1px solid #cad2da;
    }
    .wy-table-list thead {
      tr.wy-sub-header {
        > th:first-child {
          //border-bottom: 1px solid #CAD2DA;
          border-right: none;
          padding-left: 15px !important;
          text-align: center;
          &::after {
            display: none;
          }
        }
      }
    }
  }
  .wy-table-list .wy-table-main thead .wy-sub-header th {
    background-color: #f5f8fa !important;
    //border-bottom: 1px solid #CAD2DA;
    color: var(--site-font-secondary-color);
    font-size: 14.5px;
    font-weight: 500;
  }
  .wy-table-list .wy-table-main thead th:nth-last-child(2) {
    border-right: none !important;
  }

  .header-icon-info {
    display: inline-block;
    background: url('../../assets/icons/icon_info.svg') no-repeat;
    width: 18px;
    height: 18px;
    background-position-y: 4px;
    background-size: 87%;
    position: relative;
    bottom: 6px !important;
    margin-left: 0.45vw;
  }
  .filter-button {
    border-radius: 20px;
    padding: 10px 20px;
    color: var(--site-font-secondary-color);
    font-size: 16px;
    font-weight: 500;
    font-family: Helvetica Neue, Helvetica;
    background-color: #fff;
    margin-right: 0.5vw;
    border: 1px solid #dcdfe6;
  }
  .filter-button.selected {
    background: #0c2837 !important;
    border-color: #dcdfe6 !important;
    color: #fcfeff !important;
  }

  .filter-button:focus,
  .filter-button:hover {
    background: #f4f6f8;
    border-color: #cad2da;
    color: #0c2837;
  }
  .button-flex {
    display: flex;
    flex-direction: column-reverse;
    // align-items: center;
    height: 26px;
    padding-bottom: 12px;
    .question-mark {
      display: inline-block;
      width: 20px;
      padding-top: 3px;
      margin-left: 0px;

      .el-question-mark {
        cursor: pointer;
        color: #214586;
        height: 30px;
        border-radius: 1px;
        border: 0px;
        padding: 2px 5px 8px 5px;
        background-color: transparent;
      }

      .el-question-mark:hover {
        color: #214586;
      }

      .el-button:focus,
      .el-button:hover {
        background-color: transparent;
      }
    }
  }
  .exp-and-auto-page-title-small {
    color: var(--site-font-secondary-color);
    font-size: 15px;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 400;
  }
  .exp-and-auto-page-title {
    // margin-bottom: 0.8vw;
    color: var(--site-font-secondary-color);
    font-size: 25px;
    font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
    font-weight: 600;
  }

  .panel-component-section {
    min-height: calc(100% - 20px);
    padding-right: 0px;
    // margin-bottom: -25px;
    background: #fff;
    border: 1px solid #cad2da;
    width: 100%;
    height: 100%;
    min-height: 425px;
    border-radius: 5px;
    // margin-top: 20px;
    // margin-right: 0px;
    // margin-right: 20px;

    .el-loading-mask {
      height: auto !important;
      border-radius: 5px;
      // top: unset !important;
    }
    .full-size .el-loading-mask {
      top: 66px;
      height: calc(100% - 64px) !important;
    }
    .bottom-right-flex {
      .chart-header {
        display: flex;
        .compare-group-by {
          margin-right: 20px;
          margin-left: auto;
        }
      }
    }
  }
  .components-container {
    width: calc(100% - 3px);
    margin-top: 25px;
    margin-bottom: 50px;
    display: grid;
    column-gap: 25px;
    row-gap: 25px;
    display: grid;
    grid-gap: 1rem;
    grid-template-columns: 50% calc(50% - 15px);
    // grid-template-columns: 50%;
    grid-template-areas: 'item item';

    .wy-exp-and-auto-page-row > div:last-child {
      margin-right: 0px;
    }
  }
  .wy-page-tooltip .info-icon {
    right: -5px;
    top: 5px;
  }
}

@media screen and (min-width: 2000px) {
  .wy-experience-and-automation-page {
    // .panel-component-section-force:nth-child(even) {
    //   width: calc(100% - 150px);
    // }
    .components-container .panel-component-section {
      .panel-title {
        font-size: 0.9vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 0.9vw;
      }
      .value-previous-days {
        //padding-left: 0.8vw; OC-684
        line-height: 1.6vw;
        font-size: 0.9vw;
        height: 1.6vw;
        padding: 0.2vw 0.9vw 0.2vw 1.4vw;
      }
      .arrow-up {
        background-position-y: 0.58vw;
        background-position-x: 12.6px;
        background-size: 0.68vw;
      }
      .arrow-down {
        background-position-y: 0.58vw;
        background-position-x: 12.6px;
        background-size: 0.68vw;
      }
      .value-start-period {
        line-height: 1.6vw;
        font-size: 0.9vw;
        height: 1.6vw;
        padding: 0.2vw 0.9vw 0.2vw 1.4vw;
      }
      .change-over-time-texts {
        font-size: 0.66vw;
      }
      .bottom-flex-title {
        font-size: 16px;
      }
      .panel-top-flex {
        // border-right: 1px #CAD2DA solid;
        // width: 337px;
        width: 100%;
        height: 120px;
      }
      .panel-top--title {
        font-size: 14.5px;
      }
      .panel-body-flex {
        width: 100%;
        // height: 300px;
        display: flex;
        // flex-direction: column;
        .div-pie-section {
          width: 40%;
        }
        .div-column-section {
          width: 60%;
        }
      }
      .panel-bottom-flex {
        .chart-legend {
          width: 100%;
          height: 50px;
        }
      }
      .panel-component-section {
        min-height: calc(100% - 20px);
        padding-right: 0px;
        // margin-bottom: 10px;
        border: 1px solid #cad2da;
        width: 50%;
        height: 100%;
        border-radius: 5px;
        margin-top: 20px;
        margin-right: 20px;
        .panel-top-flex {
          // border-right: 1px #CAD2DA solid;
          // width: 337px;
          width: 100%;
          height: 100px;
        }
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px;
          width: calc(100% - 14px);
          .stats,
          .stats-title {
            padding-left: 2px;
          }
        }
      }

      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              // padding-left: 25px;
              // padding-left: 16px;
              // width: 9.7vw;

              .stats {
                font-size: 1.6vw;
              }
              .stats-title {
                font-size: 0.817vw;
                margin-bottom: 0.3vw;
              }
            }
            .tab-section.selected {
              border-bottom: 0.2vw #000 solid;
            }
          }
        }
      }
    }
  }
}
@media screen and (min-width: 1400px) and (max-width: 1600px) {
  .change-over-time-texts {
    font-size: 12px !important;
  }
}
@media screen and (min-width: 1085px) and (max-width: 1400px) {
  .components-container {
    grid-template-columns: 100% !important;
    grid-template-areas: unset !important;
  }
  .wy-experience-and-automation-page {
    .exp-and-auto-page-title-small {
      font-size: 1.3vw;
    }
    // .exp-and-auto-page-title{
    //   // margin-bottom: 0.8vw;
    //   font-size: 2.3vw;
    // }
    .filter-button {
      font-size: 1.3vw;
    }
    // .components-container{
    //   grid-template-columns: unset!important;
    // }
    .components-container .panel-component-section {
      display: block;

      .panel-title {
        font-size: 1.5vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 1.2vw;
      }
      .panel-component-section {
        width: calc(100% - 2px);
        height: 100%;
      }

      .value-previous-days {
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2vw;
        padding-right: 10px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .value-start-period {
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 2vw;
        padding-right: 10px;
        line-height: 2.9vw;
        height: 2.8vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .arrow-up {
        background-position-y: 0.8vw;
        background-position-x: 8px;
        background-size: 1.08vw;
      }
      .arrow-down {
        background-position-y: 0.8vw;
        background-position-x: 8px;
        background-size: 1.08vw;
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px !important;
          width: calc(100% - 14px) !important;
        }
      }
      .overview-table td .tab-section {
        width: 100% !important;
      }
      .panel-top--title {
        font-size: 14.5px;
      }

      .change-over-time-texts {
        width: 55%;
        height: 36px;
        font-size: 1.2vw;
        font-family: Helvetica Neue, Helvetica;
        color: var(--site-font-tertiary-color);
        font-weight: normal;
        padding: 0 10px 0 10px;
      }
      .bottom-flex-title {
        padding: 15px;
        font-size: 1.2vw;
        font-weight: medium;
        color: var(--site-font-secondary-color);
      }
      .wy-panel-top-section .overview-table td .tab-section.selected {
        border-bottom: 0.3vw #0c283f solid !important;
      }
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          border-bottom: 1px #cad2da solid;
          width: 100%;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              padding: 18px 14px;
              padding-right: 0px !important;
              cursor: pointer;

              &:hover {
                background: #f4f6f8;
              }

              .stats {
                .section-value {
                  font-size: 2.4vw;
                  color: var(--site-font-secondary-color);
                }
                margin-right: 5px;
              }
              .stats-title {
                font-size: 1.4vw;
                color: var(--site-font-tertiary-color);
              }
              svg.ct-chart-line {
                width: 100% !important;
              }
            }
          }
        }
      }

      .panel-bottom-flex {
        height: 50px;
      }
    }
  }
}
@media screen and (max-width: 1085px) {
  .components-container {
    grid-template-columns: 100% !important;
    grid-template-areas: unset !important;
    min-width: 500px;
  }
  .wy-experience-and-automation-page {
    .exp-and-auto-page-title-small {
      font-size: 1.3vw;
    }
    // .exp-and-auto-page-title{
    //   // margin-bottom: 0.8vw;
    //   font-size: 2.3vw;
    // }
    .filter-button {
      font-size: 1.3vw;
    }
    // .components-container{
    //   grid-template-columns: unset!important;
    // }
    .components-container .panel-component-section {
      display: block;

      .panel-title {
        font-size: 1.5vw;
      }
      span.panel-type {
        padding: 1vw;
        font-size: 1.2vw;
      }
      .panel-component-section {
        width: calc(100% - 2px);
        height: 100%;
      }

      .value-previous-days {
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 1.9vw;
        padding-right: 9px;
        line-height: 2.9vw;
        height: 2.9vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .value-start-period {
        // width: 65px;
        display: inline-block;
        // height: 36px;
        border: 1px solid transparent;
        padding-left: 1.9vw;
        padding-right: 9px;
        line-height: 2.9vw;
        height: 2.9vw;
        text-align: right;
        font-size: 1.8vw;
      }
      .arrow-up {
        background-position-y: 0.84vw;
        background-position-x: 6px;
        background-size: 1.07vw;
      }
      .arrow-down {
        background-position-y: 0.84vw;
        background-position-x: 6px;
        background-size: 1.07vw;
      }
      .VueCarousel-slide:first-child {
        .tab-section {
          padding-left: 14px !important;
          width: calc(100% - 14px) !important;
        }
      }
      .overview-table td .tab-section {
        width: 100% !important;
      }
      .panel-top--title {
        font-size: 14.5px;
      }

      .change-over-time-texts {
        width: 55%;
        height: 36px;
        font-size: 1.2vw;
        font-family: Helvetica Neue, Helvetica;
        color: var(--site-font-tertiary-color);
        font-weight: normal;
        padding: 0 10px 0 10px;
      }
      .bottom-flex-title {
        padding: 15px;
        font-size: 1.2vw;
        font-weight: medium;
        color: var(--site-font-secondary-color);
      }
      .wy-panel-top-section .overview-table td .tab-section.selected {
        border-bottom: 0.3vw #0c283f solid !important;
      }
      .wy-panel-top-section {
        .overview-table {
          padding-left: 0px;
          border-bottom: 1px #cad2da solid;
          width: 100%;
          td {
            .tab-section {
              border-bottom: 0.2vw transparent solid;
              text-align: left;
              padding: 18px 14px;
              padding-right: 0px !important;
              cursor: pointer;

              &:hover {
                background: #f4f6f8;
              }

              .stats {
                .section-value {
                  font-size: 2.4vw;
                  color: var(--site-font-secondary-color);
                }
                margin-right: 5px;
              }
              .stats-title {
                font-size: 1.4vw;
                color: var(--site-font-tertiary-color);
              }
              svg.ct-chart-line {
                width: 100% !important;
              }
            }
          }
        }
      }

      .panel-bottom-flex {
        height: 50px;
      }
    }
  }
}
</style> -->
